import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import useLanguage from '../../../hooks/useLanguage';

const MarketWatchSettingsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('general');
  const { t, isRTL } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className={`bg-card rounded-lg shadow-xl border border-border w-96 max-h-[600px] ${isRTL ? 'rtl' : ''}`}
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">{t('marketWatchSettings') || 'Piyasa İzleme Ayarları'}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground hover:text-foreground" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('general')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'general' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('general') || 'Genel'}
          </button>
          <button
            onClick={() => setActiveTab('display')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'display' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('display') || 'Görünüm'}
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-4 max-h-[400px] overflow-y-auto">
          {activeTab === 'general' && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground mb-3">{t('generalSettings') || 'Genel Ayarlar'}</h3>
              
              {/* Auto Refresh */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-foreground">{t('autoRefresh') || 'Otomatik Yenileme'}</div>
                  <div className="text-xs text-muted-foreground">{t('autoRefreshDesc') || 'Fiyatları otomatik olarak yenile'}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* Sound Alerts */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-foreground">{t('soundAlerts') || 'Ses Uyarıları'}</div>
                  <div className="text-xs text-muted-foreground">{t('soundAlertsDesc') || 'Fiyat değişimleri için ses uyarıları'}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* Update Interval */}
              <div>
                <div className="text-sm text-foreground mb-2">{t('updateInterval') || 'Güncelleme Aralığı'}</div>
                <select className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="1000">1 {t('second') || 'saniye'}</option>
                  <option value="2000" selected>2 {t('seconds') || 'saniye'}</option>
                  <option value="5000">5 {t('seconds') || 'saniye'}</option>
                  <option value="10000">10 {t('seconds') || 'saniye'}</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'display' && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground mb-3">{t('displaySettings') || 'Görünüm Ayarları'}</h3>
              
              {/* Show Spread */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-foreground">{t('showSpread') || 'Spread Göster'}</div>
                  <div className="text-xs text-muted-foreground">{t('showSpreadDesc') || 'Alış-satış farkını göster'}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* Show Volume */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-foreground">{t('showVolume') || 'Hacim Göster'}</div>
                  <div className="text-xs text-muted-foreground">{t('showVolumeDesc') || 'İşlem hacmini göster'}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* Decimal Places */}
              <div>
                <div className="text-sm text-foreground mb-2">{t('decimalPlaces') || 'Ondalık Basamak'}</div>
                <select className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5" selected>5</option>
                </select>
              </div>

              {/* Color Theme */}
              <div>
                <div className="text-sm text-foreground mb-2">{t('colorTheme') || 'Renk Teması'}</div>
                <select className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="default" selected>{t('default') || 'Varsayılan'}</option>
                  <option value="green-red">{t('greenRed') || 'Yeşil-Kırmızı'}</option>
                  <option value="blue-orange">{t('blueOrange') || 'Mavi-Turuncu'}</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('cancel') || 'İptal'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
          >
            {t('save') || 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketWatchSettingsModal;